import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, Building2, MapPin, User, Phone, Mail, Edit2, Save, X,
  Plus, Calendar, TrendingUp, IndianRupee, AlertTriangle, CheckCircle,
  RefreshCw, ChevronRight, Search
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['Affordable', 'Mid', 'Premium', 'Luxury', 'Launch', 'Auction', 'NRI'];
const STATUSES = ['Active', 'Inactive', 'Blacklisted'];

const BuilderDetail = ({ user }) => {
  const navigate = useNavigate();
  const { builderId } = useParams();
  const isNew = builderId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [builder, setBuilder] = useState({
    builder_name: '',
    city: '',
    category: '',
    spoc_name: '',
    spoc_contact: '',
    spoc_email: '',
    status: 'Active',
    assigned_bd_owner: '',
  });
  const [projects, setProjects] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [bdOwners, setBdOwners] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [projectSearch, setProjectSearch] = useState('');
  const [meetingSearch, setMeetingSearch] = useState('');

  // Filtered projects based on search
  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return projects;
    const search = projectSearch.toLowerCase();
    return projects.filter(p => 
      p.project_name?.toLowerCase().includes(search) ||
      p.location?.toLowerCase().includes(search) ||
      p.stage?.toLowerCase().includes(search)
    );
  }, [projects, projectSearch]);

  // Filtered meetings based on search
  const filteredMeetings = useMemo(() => {
    if (!meetingSearch.trim()) return meetings;
    const search = meetingSearch.toLowerCase();
    return meetings.filter(m => 
      m.discussion_summary?.toLowerCase().includes(search) ||
      m.next_action_item?.toLowerCase().includes(search) ||
      m.meeting_type?.toLowerCase().includes(search)
    );
  }, [meetings, meetingSearch]);

  useEffect(() => {
    fetchBdOwners();
    if (!isNew) {
      fetchBuilder();
      fetchProjects();
      fetchMeetings();
    }
  }, [builderId]);

  const fetchBdOwners = async () => {
    try {
      const res = await api.getBDOwners();
      setBdOwners(res.data.bd_owners || []);
    } catch (error) {
      console.error('Failed to load BD owners:', error);
    }
  };

  const fetchBuilder = async () => {
    setLoading(true);
    try {
      const res = await api.getBuilder(builderId);
      setBuilder(res.data);
    } catch (error) {
      console.error('Failed to load builder:', error);
      toast.error('Builder not found');
      navigate('/bd/builders');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.getBDProjects({ builder_id: builderId });
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const fetchMeetings = async () => {
    try {
      const res = await api.getBDMeetings({ builder_id: builderId });
      setMeetings(res.data.meetings || []);
    } catch (error) {
      console.error('Failed to load meetings:', error);
    }
  };

  const handleSave = async () => {
    if (!builder.builder_name.trim()) {
      toast.error('Builder name is required');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const res = await api.createBuilder(builder);
        toast.success('Builder created successfully');
        navigate(`/bd/builders/${res.data.builder_id}`);
      } else {
        await api.updateBuilder(builderId, builder);
        toast.success('Builder updated successfully');
        setEditing(false);
        fetchBuilder();
      }
    } catch (error) {
      console.error('Failed to save builder:', error);
      toast.error(error.response?.data?.detail || 'Failed to save builder');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${value.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
      case 'Inactive':
        return <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>;
      case 'Blacklisted':
        return <Badge className="bg-red-100 text-red-700">Blacklisted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStageBadge = (stage) => {
    const colors = {
      'Prospect': 'bg-slate-100 text-slate-600',
      'Intro Meeting Done': 'bg-blue-100 text-blue-700',
      'Commercial Discussion': 'bg-amber-100 text-amber-700',
      'Terms Locked': 'bg-purple-100 text-purple-700',
      'Agreement Signed': 'bg-emerald-100 text-emerald-700',
      'Live': 'bg-green-100 text-green-700',
      'Inactive': 'bg-red-100 text-red-700',
    };
    return <Badge className={colors[stage] || 'bg-slate-100'}>{stage}</Badge>;
  };

  const getRiskBadge = (risk) => {
    switch (risk) {
      case 'High':
        return <Badge className="bg-red-100 text-red-700">🔴 High</Badge>;
      case 'Medium':
        return <Badge className="bg-amber-100 text-amber-700">🟡 Medium</Badge>;
      default:
        return <Badge className="bg-emerald-100 text-emerald-700">🟢 Low</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/bd/builders')}
              >
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">
                  {isNew ? 'New Builder' : builder.builder_name}
                </h1>
                {!isNew && (
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(builder.status)}
                    {builder.category && (
                      <Badge variant="outline">{builder.category}</Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {!isNew && !editing && (
              <Button onClick={() => setEditing(true)}>
                <Edit2 size={16} className="mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Edit/Create Form */}
        {editing ? (
          <Card className="p-6 mb-4">
            <h2 className="font-semibold text-lg mb-4">Builder Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Builder Name *</Label>
                <Input
                  value={builder.builder_name}
                  onChange={(e) => setBuilder({ ...builder, builder_name: e.target.value })}
                  placeholder="Enter builder name"
                />
              </div>

              <div>
                <Label>City</Label>
                <Input
                  value={builder.city || ''}
                  onChange={(e) => setBuilder({ ...builder, city: e.target.value })}
                  placeholder="Enter city"
                />
              </div>

              <div>
                <Label>Category</Label>
                <Select 
                  value={builder.category || ''} 
                  onValueChange={(v) => setBuilder({ ...builder, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={builder.status} 
                  onValueChange={(v) => setBuilder({ ...builder, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>SPOC Name</Label>
                <Input
                  value={builder.spoc_name || ''}
                  onChange={(e) => setBuilder({ ...builder, spoc_name: e.target.value })}
                  placeholder="Contact person name"
                />
              </div>

              <div>
                <Label>SPOC Contact</Label>
                <Input
                  value={builder.spoc_contact || ''}
                  onChange={(e) => setBuilder({ ...builder, spoc_contact: e.target.value })}
                  placeholder="Phone number"
                />
              </div>

              <div>
                <Label>SPOC Email</Label>
                <Input
                  type="email"
                  value={builder.spoc_email || ''}
                  onChange={(e) => setBuilder({ ...builder, spoc_email: e.target.value })}
                  placeholder="Email address"
                />
              </div>

              <div>
                <Label>Assigned BD Owner</Label>
                <Select 
                  value={builder.assigned_bd_owner || ''} 
                  onValueChange={(v) => setBuilder({ ...builder, assigned_bd_owner: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select BD owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {bdOwners.map(owner => (
                      <SelectItem key={owner.user_id} value={owner.user_id}>
                        {owner.rep_name || owner.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={handleSave} disabled={saving}>
                <Save size={16} className="mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
              {!isNew && (
                <Button variant="outline" onClick={() => {
                  setEditing(false);
                  fetchBuilder();
                }}>
                  <X size={16} className="mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <>
            {/* Metrics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Card className="p-4">
                <p className="text-xs text-slate-500 uppercase">Active Projects</p>
                <p className="text-2xl font-bold text-slate-800">{builder.total_active_projects || 0}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500 uppercase">Sales FYTD</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(builder.total_gross_sales_fytd)}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500 uppercase">Brokerage Expected</p>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(builder.total_brokerage_expected)}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-slate-500 uppercase">Outstanding</p>
                <p className={`text-xl font-bold ${builder.outstanding_amount > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                  {formatCurrency(builder.outstanding_amount)}
                </p>
              </Card>
            </div>

            {/* Contact Info */}
            <Card className="p-4 mb-4">
              <h3 className="font-semibold text-sm text-slate-500 uppercase mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {builder.spoc_name && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <User size={16} className="text-slate-400" />
                    <span>{builder.spoc_name}</span>
                  </div>
                )}
                {builder.spoc_contact && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Phone size={16} className="text-slate-400" />
                    <a href={`tel:${builder.spoc_contact}`} className="hover:text-purple-600">
                      {builder.spoc_contact}
                    </a>
                  </div>
                )}
                {builder.spoc_email && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Mail size={16} className="text-slate-400" />
                    <a href={`mailto:${builder.spoc_email}`} className="hover:text-purple-600">
                      {builder.spoc_email}
                    </a>
                  </div>
                )}
                {builder.city && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <MapPin size={16} className="text-slate-400" />
                    <span>{builder.city}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Tabs for Projects and Meetings */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="projects">Projects ({projects.length})</TabsTrigger>
                <TabsTrigger value="meetings">Meetings ({meetings.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Recent Projects */}
                  <Card className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">Recent Projects</h3>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/bd/projects/new?builder_id=${builderId}`)}
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                    {projects.slice(0, 3).map(project => (
                      <div 
                        key={project.bd_project_id}
                        className="flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/bd/projects/${project.bd_project_id}`)}
                      >
                        <div>
                          <p className="font-medium text-slate-800">{project.project_name}</p>
                          <p className="text-xs text-slate-500">{project.location}</p>
                        </div>
                        {getStageBadge(project.stage)}
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">No projects yet</p>
                    )}
                  </Card>

                  {/* Recent Meetings */}
                  <Card className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">Recent Meetings</h3>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/bd/meetings/new?builder_id=${builderId}`)}
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                    {meetings.slice(0, 3).map(meeting => (
                      <div 
                        key={meeting.meeting_id}
                        className="flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/bd/meetings/${meeting.meeting_id}`)}
                      >
                        <div>
                          <p className="font-medium text-slate-800">{meeting.meeting_type}</p>
                          <p className="text-xs text-slate-500">{meeting.meeting_date}</p>
                        </div>
                        {getRiskBadge(meeting.collection_risk_level)}
                      </div>
                    ))}
                    {meetings.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">No meetings yet</p>
                    )}
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="projects">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">All Projects</h3>
                    <Button 
                      size="sm"
                      onClick={() => navigate(`/bd/projects/new?builder_id=${builderId}`)}
                    >
                      <Plus size={14} className="mr-1" />
                      Add Project
                    </Button>
                  </div>
                  
                  {/* Search Projects */}
                  <div className="relative mb-4">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search projects by name, location, or stage..."
                      value={projectSearch}
                      onChange={(e) => setProjectSearch(e.target.value)}
                      className="pl-9"
                      data-testid="project-search-input"
                    />
                  </div>
                  
                  {projects.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Building2 size={40} className="mx-auto mb-2 text-slate-300" />
                      <p>No projects for this builder</p>
                    </div>
                  ) : filteredProjects.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Search size={40} className="mx-auto mb-2 text-slate-300" />
                      <p>No projects match "{projectSearch}"</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 mb-2">
                        {filteredProjects.length} of {projects.length} projects
                      </p>
                      {filteredProjects.map(project => (
                        <div 
                          key={project.bd_project_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                          onClick={() => navigate(`/bd/projects/${project.bd_project_id}`)}
                        >
                          <div>
                            <p className="font-medium text-slate-800">{project.project_name}</p>
                            <p className="text-sm text-slate-500">{project.location}</p>
                            <div className="flex gap-2 mt-1 text-xs text-slate-400">
                              {project.brokerage_percent && <span>{project.brokerage_percent}% brokerage</span>}
                              {project.exclusive_or_open && <span>• {project.exclusive_or_open}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStageBadge(project.stage)}
                            <ChevronRight size={16} className="text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="meetings">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">All Meetings</h3>
                    <Button 
                      size="sm"
                      onClick={() => navigate(`/bd/meetings/new?builder_id=${builderId}`)}
                    >
                      <Plus size={14} className="mr-1" />
                      Log Meeting
                    </Button>
                  </div>
                  
                  {/* Search Meetings */}
                  <div className="relative mb-4">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search meetings by type, summary, or action..."
                      value={meetingSearch}
                      onChange={(e) => setMeetingSearch(e.target.value)}
                      className="pl-9"
                      data-testid="meeting-search-input"
                    />
                  </div>
                  
                  {meetings.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar size={40} className="mx-auto mb-2 text-slate-300" />
                      <p>No meetings logged</p>
                    </div>
                  ) : filteredMeetings.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Search size={40} className="mx-auto mb-2 text-slate-300" />
                      <p>No meetings match "{meetingSearch}"</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 mb-2">
                        {filteredMeetings.length} of {meetings.length} meetings
                      </p>
                      {filteredMeetings.map(meeting => (
                        <div 
                          key={meeting.meeting_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                          onClick={() => navigate(`/bd/meetings/${meeting.meeting_id}`)}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-800">{meeting.meeting_type}</p>
                              <Badge variant="outline" className="text-xs">{meeting.mode}</Badge>
                            </div>
                            <p className="text-sm text-slate-500">{meeting.meeting_date}</p>
                            <p className="text-xs text-slate-400 mt-1 max-w-md truncate">
                              Next: {meeting.next_action_description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getRiskBadge(meeting.collection_risk_level)}
                            {meeting.next_action_status === 'Completed' && (
                              <CheckCircle size={16} className="text-emerald-500" />
                            )}
                            <ChevronRight size={16} className="text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default BuilderDetail;
